import * as React from "react";
import classNames from "classnames";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import {
	DropTarget,
	DropTargetConnector,
	DropTargetMonitor,
	ConnectDropTarget
} from "react-dnd";
import { StoreState } from "../../reducers/root-reducer";
import {
	WithStyles,
	Theme,
	createStyles,
	withStyles,
	Typography,
	Tooltip
} from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import { Table } from "../../api/tables";

import { Reservation } from "../../api/reservations";
import { DropTargetData } from "./day-overview";
import { ConnectedComposerTableReservation } from "./composer-table-reservation";

import {
	addTableToLinkGroup,
	removeTableFromLinkGroup,
	deactivateTableLinkMode
} from "../../actions/store/app-actions";
import { getGroupDataForTable } from "../../util/table-link-utility";
import {
	getSelectedOccupiedDay,
	getReservationsForTable,
	getPeopleCountSumForTable,
	getReservationsForDay,
	getTotalSoftLimitForTable
} from "../../util/general-utility";
import { OccupiedDay } from "../../api/occupied-days";
import { GRIDV, GRIDH } from "../admin-settings/table-layout-composer/composer";
import { th } from "date-fns/esm/locale";

interface GroupData {
	groupIndex?: number;
	color?: string;
}

interface State {
	reservations: Reservation[];
	peopleCount: number;
	softLimit: number;
	groupData: GroupData;
	color: string;
}

export interface ComposerTableProps {
	id: string;
	displayName: string;
	peopleSoftLimit: number;
	x: number;
	y: number;
	width: number;
	height: number;
	unitSize: number;
}

interface ConnectedComposerTableProps extends WithStyles<typeof styles> {
	tables: Table[];
	reservations: Reservation[];
	occupiedDays: OccupiedDay[];
	selectedDate: Date;
	tableLinkModeActive: boolean;
	tableLinkDeletionModeActive: boolean;
	canDrop?: boolean;
	isOver?: boolean;
	connectDropTarget?: ConnectDropTarget;
	tempTableLinkGroups: string[][];
	addTableToLinkGroup: (id: string) => void;
	removeTableFromLinkGroup: (id: string) => void;
	deactivateTableLinkMode: () => void;
}

type Props = ComposerTableProps &
	ConnectedComposerTableProps &
	InjectedTranslateProps;

export class ComposerTable extends React.Component<Props, State> {
	public state: State = {
		reservations: [],
		peopleCount: 0,
		softLimit: 0,
		groupData: null,
		color: ""
	};

	public componentDidMount() {
		this.updateStateFromProps();
	}

	public componentWillReceiveProps(nextProps: Props) {
		if (
			nextProps.reservations !== this.props.reservations ||
			nextProps.tables !== this.props.tables ||
			nextProps.selectedDate !== this.props.selectedDate ||
			nextProps.occupiedDays !== this.props.occupiedDays ||
			nextProps.tableLinkModeActive !== this.props.tableLinkModeActive ||
			nextProps.tableLinkDeletionModeActive !==
				this.props.tableLinkDeletionModeActive ||
			JSON.stringify(nextProps.tempTableLinkGroups) !==
				JSON.stringify(this.props.tempTableLinkGroups)
		) {
			this.updateStateFromProps(nextProps);
		}
	}

	public shouldComponentUpdate(nextProps: Props, nextState: State) {
		return (
			nextProps.isOver !== this.props.isOver ||
			nextState.color !== this.state.color ||
			nextState.groupData !== this.state.groupData ||
			nextState.peopleCount !== this.state.peopleCount ||
			nextState.reservations !== this.state.reservations ||
			nextState.softLimit !== this.state.softLimit
		);
	}

	private handleClick = () => {
		const {
			id,
			tableLinkModeActive,
			tableLinkDeletionModeActive,
			addTableToLinkGroup,
			removeTableFromLinkGroup
		} = this.props;

		if (tableLinkModeActive) {
			addTableToLinkGroup(id);
		} else if (tableLinkDeletionModeActive) {
			removeTableFromLinkGroup(id);
		}
	};

	private handleContextMenu = () => {
		this.props.deactivateTableLinkMode();
	};

	private updateStateFromProps = (props = this.props) => {
		const { id, reservations, tables, selectedDate } = props;

		if (tables.length === 0) return;

		const groups = this.getGroupData(props);
		const groupData = groups.groupData;
		const softLimit = getTotalSoftLimitForTable(id, tables, groups.groups);
		const color =
			groupData && groupData.color ? groupData.color : "#7B7B7B";

		this.setState({
			softLimit,
			groupData,
			color
		});

		if (reservations.length > 0) {
			const selectedReservations = getReservationsForDay(
				reservations,
				selectedDate
			);

			const tableReservations = getReservationsForTable(
				id,
				selectedReservations,
				selectedDate
			);
			const peopleCount = getPeopleCountSumForTable(
				id,
				selectedReservations,
				groups.groups
			);

			this.setState({
				reservations: tableReservations,
				peopleCount
			});
		} else {
			this.setState({
				reservations: [],
				peopleCount: 0,
			});
		}
	};

	private getGroupData(props = this.props) {
		const {
			id,
			occupiedDays,
			selectedDate,
			tableLinkModeActive,
			tableLinkDeletionModeActive,
			tempTableLinkGroups
		} = props;
		let rawGroupData: string[][] = [[]];

		if (tableLinkModeActive || tableLinkDeletionModeActive) {
			rawGroupData = tempTableLinkGroups;
		} else {
			const occupiedDay = getSelectedOccupiedDay(
				occupiedDays,
				selectedDate
			);

			if (occupiedDay) {
				rawGroupData = occupiedDay.linkedTables;
			}
		}

		return {
			groupData: getGroupDataForTable(id, rawGroupData),
			groups: rawGroupData
		};
	}

	public render() {
		const {
			classes,
			width,
			height,
			x,
			y,
			displayName,
			connectDropTarget,
			isOver
		} = this.props;

		const {
			reservations,
			peopleCount,
			softLimit,
			groupData,
			color
		} = this.state;

		return (
			connectDropTarget &&
			connectDropTarget(
				<div
					className={classNames(classes.root, {
						[classes.rootDNDHover]: isOver,
						[classes.semiTransparent]: displayName.startsWith("P") && !(groupData && groupData.color),
					})}
					onClick={this.handleClick}
					onContextMenu={this.handleContextMenu}
					style={{
						top: `${(y * 100) / GRIDV}%`,
						left: `${(x * 100) / GRIDH}%`,
						width: `${(width * 100) / GRIDH}%`,
						height: `${(height * 100) / GRIDV}%`,
						borderColor: color,
						backgroundColor:
							groupData && groupData.color
								? `${color}22`
								: "white"
					}}
				>
					<Typography
						component="span"
						className={classes.displayName}
						style={{
							backgroundColor:
								groupData && groupData.color
									? `${color}66`
									: "#7B7B7B66"
						}}
					>
						{displayName}
					</Typography>
					<div className={classes.reservations}>
						{reservations.length > 0 &&
							reservations.map(reservation => (
								<ConnectedComposerTableReservation
									key={reservation.id}
									tableReservationCount={reservations.length}
									{...reservation}
								/>
							))}
					</div>
					{peopleCount > 0 && (
						<div className={classes.peopleCountIndicator}>
							<Typography
								className={classes.peopleCountIndicatorLabel}
							>
								{peopleCount}
							</Typography>
							<div
								className={
									classes.peopleCountIndicatorBackground
								}
								style={{
									backgroundColor: `${color}44`,
									borderColor: color,
									transform: `translate3d(${
										peopleCount > 9 ? "4" : "5"
									}0%, 50%, 0)`
								}}
							/>
							<div
								className={
									classes.peopleCountIndicatorBackgroundBackdrop
								}
								style={{
									transform: `translate3d(${
										peopleCount > 9 ? "4" : "5"
									}0%, 50%, 0)`
								}}
							/>
						</div>
					)}
					<div
						className={classNames(
							classes.softLimitExceededBackground,
							{
								[classes.softLimitExceededBackgroundVisible]:
									peopleCount > softLimit
							}
						)}
					>
						<div
							className={
								classes.softLimitExceededBackgroundStripes
							}
							style={{
								background: `repeating-linear-gradient(45deg, ${color}, ${color} 1px, transparent 3px, transparent 16px)`
							}}
						/>
					</div>
				</div>
			)
		);
	}
}

@DropTarget(
	"ReservationItem",
	{
		drop(props: Props): DropTargetData {
			return { tableId: props.id };
		}
	},
	(connect: DropTargetConnector, monitor: DropTargetMonitor) => ({
		connectDropTarget: connect.dropTarget(),
		isOver: monitor.isOver(),
		canDrop: monitor.canDrop()
	})
)
export class DecoratedComposerTable extends ComposerTable {}

const mapStateToProps = (state: StoreState) => ({
	tables: state.tables,
	selectedDate: state.date,
	reservations: state.reservations,
	occupiedDays: state.occupiedDays,
	tableLinkModeActive: state.tableLinkModeActive,
	tableLinkDeletionModeActive: state.tableLinkDeletionModeActive,
	tempTableLinkGroups: state.tempTableLinkGroups
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	addTableToLinkGroup: (id: string) => dispatch(addTableToLinkGroup(id)),
	removeTableFromLinkGroup: (id: string) =>
		dispatch(removeTableFromLinkGroup(id)),
	deactivateTableLinkMode: () => dispatch(deactivateTableLinkMode())
});

const styles = (theme: Theme) =>
	createStyles({
		root: {
			position: "absolute",
			backgroundColor: "white",
			border: "2px solid #7B7B7B",
			borderRadius: 4,
			transition: "box-shadow 0.2s ease, background-color 0.3s ease",
			display: "flex",
			flexDirection: "column",
			paddingBottom: 25,

			"& > *": {
				position: "relative",
				zIndex: 10
			}
		},
		rootDNDHover: {
			boxShadow: "0 0 12px rgba(0, 0, 0, 0.2)"
		},
		semiTransparent: {
			opacity: 0.25,
		},
		label: {
			fontSize: 18,
			color: theme.palette.text.primary,
			userSelect: "none"
		},
		displayName: {
			position: "absolute",
			bottom: -4,
			left: 5,
			color: "transparent",
			display: "block",
			fontSize: "1.5rem",
			fontWeight: 400,
			backgroundColor: "#DADADA",
			textShadow: "1px 1px 1px rgba(255, 255, 255, 0.5)",
			"-webkit-background-clip": "text",
			backgroundClip: "text"
		},
		titlebar: {
			display: "flex",
			justifyContent: "space-between",
			background: theme.palette.grey[100],
			padding: "3px 10px"
		},
		reservations: {
			flexGrow: 1,
			display: "flex",
			flexWrap: "wrap",
			justifyContent: "center",
			alignItems: "center"
		},
		peopleCountIndicator: {
			position: "absolute",
			bottom: 0,
			right: 0,
			width: 64,
			height: 64,
			overflow: "hidden",
			pointerEvents: "none"
		},
		peopleCountIndicatorBackground: {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			backgroundColor: "#7B7B7B44",
			opacity: 1,
			borderRadius: "100%",
			border: "2px solid #7B7B7B",
			transform: "translate3d(50%, 50%, 0) rotate(45deg)",
			zIndex: 11
		},
		peopleCountIndicatorBackgroundBackdrop: {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			background: "white",
			borderRadius: "100%",
			transform: "translate3d(50%, 50%, 0) rotate(45deg)"
		},
		peopleCountIndicatorLabel: {
			position: "absolute",
			bottom: 1,
			right: 7,
			color: "#505050",
			zIndex: 5
		},
		softLimitExceededIndicator: {
			position: "absolute",
			top: 0,
			right: 0,
			width: 24,
			height: 24,
			borderRadius: "100%",
			backgroundColor: "#F72E05",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			boxShadow: "-1px 1px 3px rgba(55, 55, 55, 0.5)",
			transform: "translate3d(50%, -50%, 0) scale(0)",
			transition: "transform 0.3s ease"
		},
		softLimitExceededIndicatorVisible: {
			transform: "translate3d(50%, -50%, 0) scale(1)"
		},
		softLimitExceededIndicatorLabel: {
			color: "white",
			fontWeight: 500,
			pointerEvents: "none",
			userSelect: "none"
		},
		softLimiExceededIndicatorTooltip: {
			background: "#444342",
			marginLeft: 7
		},
		softLimitExceededBackground: {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			background: "white",
			opacity: 0,
			transition: "opacity 0.3s ease",
			zIndex: 1
		},
		softLimitExceededBackgroundVisible: {
			opacity: 0.3
		},
		softLimitExceededBackgroundStripes: {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			background:
				"repeating-linear-gradient(45deg, transparent, transparent 1px, #ffffff 3px, #ffffff 16px)"
		}
	});

export const ConnectedComposerTable = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(DecoratedComposerTable)
	)
);
